import liff from '@line/liff';

const LIFF_ID = import.meta.env.VITE_LIFF_ID;

export interface LiffInitResult {
  isLoggedIn: boolean;
  profile?: {
    userId: string;
    displayName: string;
    pictureUrl?: string;
    statusMessage?: string;
  };
  accessToken?: string;
  context?: any;
}

/**
 * 初始化 LIFF SDK
 */
export async function initializeLiff(): Promise<LiffInitResult> {
  try {
    // 檢查 LIFF_ID 是否設定
    if (!LIFF_ID) {
      throw new Error('LIFF_ID 未設定，請檢查環境變數');
    }

    console.log('Initializing LIFF with ID:', LIFF_ID);
    console.log('Environment:', import.meta.env.VITE_ENV);

    // 初始化 LIFF
    try {
      await liff.init({
        liffId: LIFF_ID,
        withLoginOnExternalBrowser: true, // 外部瀏覽器也自動登入
      });
    } catch (liffError: any) {
      console.error('LIFF init failed:', liffError);
      throw new Error(
        `LIFF 初始化失敗: ${liffError?.message || '未知錯誤'}。\n` +
        `請確認 LINE Developers Console 中的 Endpoint URL 是否設定為: https://liff-vendor.pages.dev`
      );
    }

    console.log('LIFF initialized successfully');

    // 檢查登入狀態
    if (!liff.isLoggedIn()) {
      console.log('User not logged in, initiating login...');
      liff.login();
      // login 會導向 LINE，所以這裡不會繼續執行
      return {
        isLoggedIn: false,
      };
    }

    // 取得用戶資料
    const profile = await liff.getProfile();
    const accessToken = liff.getAccessToken();
    const context = liff.getContext();

    console.log('LINE identity loaded');
    console.log('LIFF context loaded');

    return {
      isLoggedIn: true,
      profile: {
        userId: profile.userId,
        displayName: profile.displayName,
        pictureUrl: profile.pictureUrl,
        statusMessage: profile.statusMessage,
      },
      accessToken: accessToken || undefined,
      context,
    };
  } catch (error) {
    console.error('LIFF initialization error:', error);
    throw error;
  }
}

/**
 * 取得用戶資料
 */
export async function getUserProfile() {
  if (!liff.isLoggedIn()) {
    throw new Error('User not logged in');
  }
  return await liff.getProfile();
}

/**
 * 取得 Access Token
 */
export function getAccessToken(): string | null {
  if (!liff.isLoggedIn()) {
    return null;
  }
  return liff.getAccessToken();
}

/**
 * 取得 LIFF Context
 */
export function getContext() {
  return liff.getContext();
}

/**
 * 檢查是否在 LINE 內
 */
export function isInClient(): boolean {
  return liff.isInClient();
}

/**
 * 開啟外部連結
 */
export function openExternalUrl(url: string, external: boolean = true) {
  liff.openWindow({
    url,
    external,
  });
}

/**
 * 關閉 LIFF 視窗
 */
export function closeWindow() {
  liff.closeWindow();
}

/**
 * 分享訊息
 */
export async function shareMessage(messages: any[]) {
  if (!liff.isApiAvailable('shareTargetPicker')) {
    throw new Error('Share Target Picker is not available');
  }

  try {
    await liff.shareTargetPicker(messages);
    console.log('Message shared successfully');
  } catch (error) {
    console.error('Share failed:', error);
    throw error;
  }
}

/**
 * 發送訊息到目前對話
 */
export async function sendMessages(messages: any[]) {
  try {
    await liff.sendMessages(messages);
    console.log('Messages sent successfully');
  } catch (error) {
    console.error('Send messages failed:', error);
    throw error;
  }
}

/**
 * 登出
 */
export function logout() {
  liff.logout();
  window.location.reload();
}

/**
 * 檢查 API 是否可用
 */
export function isApiAvailable(apiName: string): boolean {
  return liff.isApiAvailable(apiName);
}

// 匯出 liff 實例以供特殊用途
export { liff };
